import externalLinkIcon from "assets/images/external-link.svg";
import classNames from "classnames";
import {format, parse} from "date-fns";
import {useState} from "react";
import {useParams} from "react-router-dom";
import {common as stringsCommon, prescriptionDetails as strings} from "strings";

import Button from "components/Button";
import ConfirmRefillRequest from "components/ConfirmRefillRequest";
import Divider from "components/Divider";
import LoadingIndicator from "components/LoadingIndicator";
import MobileNavigationHeader from "components/MobileNavigationHeader";
import PageTitle from "components/PageTitle";
import RefillPlacedModal from "components/RefillPlacedModal";
import StatusDisplay from "components/StatusDisplay";

import {getMedguides} from "liberty-core/api/medguides";
import * as models from "liberty-core/api/models";
import {usePrescriptionByScriptNumber} from "liberty-core/api/react-query/prescription";
import {useRefillRequestRegistered} from "liberty-core/api/react-query/refillRequest";
import {formatPrice, isFillOnHold} from "liberty-core/sharedFunctions";

import {useAlert} from "lib/alert";
import {useCurrentPharmacy} from "lib/useCurrentPharmacy";

import styles from "./PrescriptionDetailsPage.module.css";

export default function PrescriptionDetailsPage() {
  const [showConfirmRefillModal, setShowConfirmRefillModal] = useState(false);
  const [showRefillPlacedModal, setShowRefillPlacedModal] = useState(false);
  const [pickUpDate, setPickUpDate] = useState<Date | undefined>();
  const [pickUpTime, setPickUpTime] = useState<models.TimeString>();
  const [comments, setComments] = useState("");
  const [loadingMedInfo, setLoadingMedInfo] = useState(false);
  const alert = useAlert();

  const [pharmacy] = useCurrentPharmacy();

  const {
    mutateAsync: confirmRefillRequestMutation,
    isLoading: isRefillRequestLoading,
  } = useRefillRequestRegistered();

  const scriptNumber = useParams().id ?? "";
  const {data: prescription, isLoading} = usePrescriptionByScriptNumber(
    +scriptNumber,
  );
  const [refillResponse, setRefillResponse] = useState<models.RefillResponse[]>(
    [],
  );

  if (isLoading || !prescription) {
    return <LoadingIndicator />;
  }

  function formatDate(date: Date) {
    return format(date, "MM/dd/yyyy");
  }

  function renderMediaLayout() {
    if (!prescription) {
      return null;
    }

    return (
      <img
        src={prescription.drugPrescribed.imageUrl}
        alt={prescription?.drugPrescribed.name}
        className={classNames(styles.media, styles.media1)}
      />
    );
  }

  const statusIndices = {
    OutOfRefills: -1,
    DoNotShow: -1,
    Scheduled: 0,
    Processing: 1,
    ReadyForPickup: 2,
  };

  function renderStatusView() {
    if (!prescription) {
      return null;
    }

    const statusObjs: {status: models.PrescriptionStatus; label: string}[] = [
      {
        status: "Scheduled",
        label: `${strings.orderPlaced}${
          prescription.orderSource ? " " + prescription.orderSource : ""
        }`,
      },
      {status: "Processing", label: strings.processing},
      {
        status: "ReadyForPickup",
        label: strings.readyForPickup,
      },
    ];

    let textElement: React.ReactNode = null;
    if (prescription.status === "ReadyForPickup") {
      // If ready for pickup, display ready for pickup
      textElement = (
        <h3 className={styles.h3}>{strings.refillReadyForPickup}</h3>
      );
    } else if (
      !(
        prescription.expectedPickupDate !== null &&
        prescription.expectedPickupDate > new Date()
      )
    ) {
      // If not ready for pickup, but no expected pickup date/time is given, or the expected date/tme has passed, just say the refill is on its way
      textElement = <h3 className={styles.h3}>{strings.onItsWay}</h3>;
    } else if (
      prescription.expectedPickupDate &&
      // If not ready for pickup, and pickup date/time is in the future, display the expected pickup date ad time
      prescription.expectedPickupDate > new Date()
    ) {
      textElement = (
        <div>
          <h3 className={styles.h3} style={{marginBottom: "0px"}}>
            {strings.onItsWay}
          </h3>
          <h4>{`${strings.expectedPickup} ${format(
            prescription.expectedPickupDate,
            "Pp",
          )}`}</h4>
        </div>
      );
    }

    return (
      <div
        className={classNames(styles.whiteContainer, styles.statusContainer)}
      >
        {textElement}
        {/* TODO: Make this a text that changes depending on whether there is an error or not */}
        <StatusDisplay
          labels={statusObjs.map((o) => o.label)}
          current={statusObjs.findIndex(
            (o) => o.status === prescription.status,
          )}
          error={false} /* TODO: Replace with API call */
          hideOnDesktop={true}
        />
      </div>
    );
  }

  function renderRefillInfoView() {
    if (!prescription || !pharmacy) {
      return null;
    }

    const autoFillActive =
      prescription.isAutoFill && prescription.status !== "OutOfRefills";
    const outOfAutoRefills =
      prescription.isAutoFill && prescription.status === "OutOfRefills";
    const manuallyRefillable =
      !prescription.isAutoFill && prescription.refillable;
    const hasRefillableMessage =
      !!prescription.refillableMessage && prescription.status !== "Scheduled";
    const overrideShowMessages =
      prescription.status === "Processing" ||
      prescription.status === "Scheduled";

    if (
      !autoFillActive &&
      !outOfAutoRefills &&
      !manuallyRefillable &&
      !hasRefillableMessage &&
      !overrideShowMessages
    ) {
      return null;
    }

    return (
      <div
        className={classNames(
          styles.whiteContainer,
          styles.outOfRefillsContainer,
        )}
      >
        <div className={styles.innerInfoContainer}>
          <div className={styles.refillsRemainingContainer}>
            {prescription.refillProgram ? (
              <div className={styles.refillProgram}>
                {prescription.refillProgram}
              </div>
            ) : null}
            <div
              className={classNames(
                styles.h4,
                styles.innerInfoText,
                styles.weight700,
              )}
            >
              {prescription.refillsRemaining}
            </div>
          </div>
          <div className={styles.innerInfoButtonContainer}>
            {autoFillActive || outOfAutoRefills || manuallyRefillable ? (
              autoFillActive ? (
                <Button href={`tel:${pharmacy.phone}`}>
                  {strings.contactPharmacy}
                </Button>
              ) : (
                <Button onClick={() => setShowConfirmRefillModal(true)}>
                  {strings.requestRefill}
                </Button>
              )
            ) : null}
          </div>
        </div>
        {overrideShowMessages ? (
          <div className={styles.innerInfoBottomText}>
            {prescription.statusText}
          </div>
        ) : null}
        {hasRefillableMessage ? (
          <div className={styles.innerInfoBottomText}>
            {prescription.refillableMessage}
          </div>
        ) : null}
      </div>
    );
  }

  function renderPatientPay(cost: number, isOnHold: boolean) {
    return (
      <>
        <p className={styles.patientPayText}>{strings.patientPay}</p>
        <h3 className={classNames(styles.h3, styles.marginblockend4)}>
          {isOnHold ? "- -" : formatPrice(cost)}
        </h3>
      </>
    );
  }

  const isOnFile =
    !!prescription &&
    prescription.fills.length === 1 &&
    prescription.fills[0].quantityDispensed === 0 &&
    prescription.fills[0].dispenseDate === null;

  return (
    <div className={styles.container}>
      <MobileNavigationHeader backTo="/medications" />
      <div className={styles.col}>
        {renderMediaLayout()}
        <div className={styles.headerContainer}>
          <PageTitle>{prescription.drugPrescribed.name}</PageTitle>
          <div className={styles.costContainer}>
            {renderPatientPay(
              prescription.fills[0].patientPay,
              isFillOnHold(prescription.fills[0]),
            )}
          </div>
        </div>
      </div>

      <div className={styles.detailsRow}>
        <div className={styles.detailsContainer}>
          <div className={styles.detailsLeft}>
            <div
              className={classNames(
                styles.detailsText,
                styles.weight700,
                styles.uppercase,
              )}
            >
              {stringsCommon.rxNum}
            </div>
            <div
              className={classNames(
                styles.detailsText,
                styles.weight700,
                styles.uppercase,
              )}
            >
              {isOnFile ? strings.writtenOn : strings.lastFilled}
            </div>
            <div
              className={classNames(
                styles.detailsText,
                styles.weight700,
                styles.uppercase,
              )}
            >
              {strings.supply}
            </div>
            <div
              className={classNames(
                styles.detailsText,
                styles.weight700,
                styles.uppercase,
              )}
            >
              {strings.prescriber}
            </div>
          </div>
          <div>
            <div className={styles.detailsText}>
              {prescription.scriptNumber}
            </div>
            <div className={styles.detailsText}>
              {isOnFile
                ? formatDate(new Date(prescription.writtenDate))
                : prescription.fills[0].dispenseDate
                ? formatDate(new Date(prescription.fills[0].dispenseDate))
                : // this last case should never happen
                  ""}
            </div>
            <div className={styles.detailsText}>
              {prescription.daysSupply} {strings.days}
            </div>
            <div className={styles.detailsText}>
              {prescription.prescriber.name.firstName}
              {prescription.prescriber.name.middleInitial
                ? " " + prescription.prescriber.name.middleInitial + " "
                : " "}
              {prescription.prescriber.name.lastName}
            </div>
          </div>
        </div>
        <div className={styles.costContainerMobile}>
          {renderPatientPay(
            prescription.fills[0].patientPay,
            isFillOnHold(prescription.fills[0]),
          )}
        </div>
      </div>
      {statusIndices[prescription.status] > -1 && pharmacy?.progressEnabled
        ? renderStatusView()
        : null}
      {renderRefillInfoView()}
      <div className={classNames(styles.section, styles.instructionsContainer)}>
        <p
          className={classNames(
            styles.size20,
            styles.weight700,
            styles.uppercase,
          )}
        >
          {strings.instructions}
        </p>
        <p className={styles.text}>{prescription.instructions}</p>
      </div>
      <button
        disabled={loadingMedInfo}
        className={styles.medInfoButton}
        onClick={() => {
          if (loadingMedInfo) {
            return;
          }

          if (
            prescription.drugPrescribed.ndcNumber.length !== 11 ||
            prescription.drugPrescribed.mixture === "2"
          ) {
            alert({
              title: stringsCommon.whoops,
              message: strings.medInfoError1,
            });
          } else {
            setLoadingMedInfo(true);
            getMedguides(
              process.env.REACT_APP_MEDGUIDES_KEY ?? "",
              prescription.drugPrescribed.ndcNumber,
              strings.getLanguage(),
            )
              .then((response) => {
                window.open(
                  response.data.links.contentShortUrl.href,
                  "_blank",
                  "noopener,noreferrer",
                );
              })
              .catch(() =>
                alert({
                  title: stringsCommon.whoops,
                  message: strings.medInfoError2,
                }),
              )
              .finally(() => setLoadingMedInfo(false));
          }
        }}
      >
        {strings.medicationInformation}
        <img
          className={styles.medInfoButtonIcon}
          src={externalLinkIcon}
          alt=""
        />
      </button>
      <div
        className={classNames(styles.section, styles.previousRefillsContainer)}
      >
        <h2 className={classNames(styles.h2, styles.uppercase)}>
          {strings.fillHistory}
        </h2>
        <div className={styles.list}>
          {prescription.fills.map((fill, index) => {
            if (isOnFile) {
              return <div className={styles.entry}>{strings.onFile}</div>;
            }

            return (
              <div className={styles.list} key={index}>
                <div className={styles.entry}>
                  <div className={styles.detailsContainer}>
                    <div className={styles.detailsLeft}>
                      <div
                        className={classNames(
                          styles.detailsText,
                          styles.fillDetailsText,
                          styles.weight700,
                          styles.uppercase,
                        )}
                      >
                        {strings.quantity}
                      </div>
                      <div
                        className={classNames(
                          styles.detailsText,
                          styles.fillDetailsText,
                          styles.weight700,
                          styles.uppercase,
                        )}
                      >
                        {strings.supply}
                      </div>
                      <div
                        className={classNames(
                          styles.detailsText,
                          styles.fillDetailsText,
                          styles.weight700,
                          styles.uppercase,
                        )}
                      >
                        {strings.patientPay}
                      </div>
                    </div>
                    <div>
                      <div
                        className={classNames(
                          styles.detailsText,
                          styles.fillDetailsText,
                        )}
                      >
                        {fill.quantityDispensed}
                      </div>
                      <div
                        className={classNames(
                          styles.detailsText,
                          styles.fillDetailsText,
                        )}
                      >
                        {fill.daysSupply} {strings.days}
                      </div>
                      <div
                        className={classNames(
                          styles.detailsText,
                          styles.fillDetailsText,
                        )}
                      >
                        {isFillOnHold(fill)
                          ? "- -"
                          : formatPrice(fill.patientPay)}
                      </div>
                    </div>
                  </div>
                  {fill.dispenseDate ? (
                    <div>
                      <p className={styles.text}>
                        {formatDate(new Date(fill.dispenseDate))}
                      </p>
                    </div>
                  ) : null}
                </div>
                {index !== prescription.fills.length - 1 ? (
                  <Divider className={styles.divider} />
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
      <ConfirmRefillRequest
        visible={showConfirmRefillModal}
        onPickDate={(dateChosen) => {
          setPickUpDate(dateChosen);
        }}
        onPickTime={(timeChosen: models.TimeString) => {
          setPickUpTime(timeChosen);
        }}
        onClickCancel={() => {
          setShowConfirmRefillModal(false);
        }}
        onClickRequest={() => {
          if (pickUpDate && pickUpTime) {
            const pickUpDateTime = parse(pickUpTime, "HH:mm", pickUpDate);
            const refillRequestData = {
              scriptNumbers: [prescription.scriptNumber],
              pickupDateTime: pickUpDateTime.toISOString(),
              comment: comments,
            };
            confirmRefillRequestMutation(refillRequestData)
              .then((response) => {
                setRefillResponse(response);
                setShowRefillPlacedModal(true);
              })
              .catch(() => {
                alert({
                  title: stringsCommon.whoops,
                  message: strings.genericErrorMessage,
                });
              })
              .finally(() => {
                setShowConfirmRefillModal(false);
                setPickUpDate(undefined);
                setPickUpTime("");
              });
          }
          return;
        }}
        med={[
          {
            name: prescription.drugPrescribed.name,
            quantity: prescription.fills[0].quantityDispensed, // TODO: what should we be using here?
            isOnFile,
          },
        ]}
        datePicked={pickUpDate}
        timePicked={pickUpTime}
        loading={isRefillRequestLoading}
        comment={comments}
        onComment={setComments}
      />
      <RefillPlacedModal
        medicationList={[
          {
            scriptNumber: prescription.scriptNumber,
            name: prescription.drugPrescribed.name,
            imageUrl: "/temp-prescription-photo.svg",
          },
        ]}
        refillResponse={refillResponse}
        visible={showRefillPlacedModal}
        onClickClose={() => setShowRefillPlacedModal(false)}
      />
    </div>
  );
}
